import { useEffect, useRef, useState } from 'react';
import { Pencil, Loader2, RotateCcw } from 'lucide-react';
import NombreOido from './NombreOido';

/**
 * Añade edición a mano sobre un nombre de llamada.
 *
 * Cómo se ve el nombre no se decide aquí: eso lo sigue haciendo `NombreOido`,
 * que distingue el dato de Hostify del que se oyó en la conversación y lo marca
 * en cursiva con su icono. Este componente solo pone encima lo que hace falta
 * para corregirlo —el lápiz, el campo, el deshacer— y una tercera procedencia
 * que antes no existía: lo escrito a mano, que manda sobre las otras dos.
 *
 * Separarlo así evita el problema de haberlo reescrito: con dos componentes
 * pintando nombres, cualquier cambio en cómo se señala un nombre oído habría
 * que hacerlo dos veces, y tarde o temprano se haría en uno solo.
 *
 * La edición se abre con el lápiz y no pulsando la celda: estas celdas viven en
 * filas que abren el detalle al pulsarlas, y robar ese gesto convertiría abrir
 * una llamada en un juego de puntería.
 */
export default function CampoEditable({
  confirmado,
  oido,
  corregido = false,
  vacio,
  ficha,
  etiqueta,
  onGuardar,
  onRestaurar,
}: {
  /** El valor que trae la llamada, ya con la corrección aplicada si la hay. */
  confirmado?: string | null;
  /** Lo que se dijo en la llamada, si el teléfono no estaba fichado. */
  oido?: string | null;
  /** Si el valor de arriba viene de una corrección manual. */
  corregido?: boolean;
  /** Qué poner cuando no hay ni dato ni nada oído. */
  vacio?: string;
  /** Ficha del huésped, para enlazar el nombre. Solo aplica al del huésped. */
  ficha?: string | null;
  /** Para el aria-label: «Editar el huésped», «Editar el alojamiento». */
  etiqueta: string;
  onGuardar: (valor: string) => Promise<void>;
  /** Devuelve el campo a lo que diga el pipeline. Solo si está corregido. */
  onRestaurar?: () => Promise<void>;
}) {
  const [editando, setEditando] = useState(false);
  const [texto, setTexto] = useState('');
  const [ocupado, setOcupado] = useState(false);
  const campo = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (editando) campo.current?.select();
  }, [editando]);

  const abrir = (e: React.MouseEvent) => {
    e.stopPropagation();
    setTexto(confirmado ?? oido ?? '');
    setEditando(true);
  };

  const guardar = async () => {
    if (ocupado) return;
    const limpio = texto.trim();
    // Guardar lo mismo que ya había no merece un viaje al servidor.
    if (limpio === (confirmado ?? '')) return setEditando(false);
    setOcupado(true);
    try {
      await onGuardar(limpio);
      setEditando(false);
    } finally {
      setOcupado(false);
    }
  };

  const restaurar = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!onRestaurar || ocupado) return;
    setOcupado(true);
    try {
      await onRestaurar();
      setEditando(false);
    } finally {
      setOcupado(false);
    }
  };

  if (editando) {
    return (
      <span className="inline-flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
        <input
          ref={campo}
          className="input py-1 px-2 text-sm min-w-0 w-full max-w-[190px]"
          value={texto}
          disabled={ocupado}
          onChange={(e) => setTexto(e.target.value)}
          onBlur={guardar}
          onKeyDown={(e) => {
            if (e.key === 'Enter') guardar();
            // Escape descarta. Para borrar el valor se deja vacío y se acepta:
            // eso es una decisión, no un arrepentimiento.
            if (e.key === 'Escape') setEditando(false);
          }}
        />
        {ocupado && <Loader2 size={13} className="animate-spin text-slate-400 shrink-0" />}
      </span>
    );
  }

  return (
    <span className="inline-flex items-center gap-1 group/campo min-w-0">
      <span
        className="truncate"
        title={corregido ? 'Escrito a mano; manda sobre lo que diga Hostify' : undefined}
      >
        <NombreOido
          confirmado={confirmado}
          oido={oido}
          ficha={ficha}
          {...(vacio !== undefined ? { vacio } : {})}
        />
      </span>

      {corregido && <Pencil size={11} className="text-slate-400 shrink-0" aria-label="Corregido a mano" />}

      {/* El lápiz aparece al pasar por encima para no llenar la tabla de iconos,
          pero se mantiene visible al enfocarlo con el teclado. */}
      <button
        type="button"
        onClick={abrir}
        aria-label={etiqueta}
        title={etiqueta}
        className="opacity-0 group-hover/campo:opacity-100 focus:opacity-100 transition-opacity
                   text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 shrink-0"
      >
        <Pencil size={12} />
      </button>

      {corregido && onRestaurar && (
        <button
          type="button"
          onClick={restaurar}
          aria-label="Deshacer la corrección"
          title="Deshacer la corrección y volver a lo que diga el pipeline"
          className="opacity-0 group-hover/campo:opacity-100 focus:opacity-100 transition-opacity
                     text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 shrink-0"
        >
          <RotateCcw size={11} />
        </button>
      )}
    </span>
  );
}
